import { DeviceType } from '@/types/common';
import { isMobile } from 'is-mobile';

export const isMobileDevice = () => {
  if (typeof window !== 'undefined') {
    return window.innerWidth < 700;
  }
  return isMobile();
};

export const isAndroid = () => {
  return /Android/i.test(navigator.userAgent);
};

/**
 * Resolve the active platform from the user agent, not the viewport.
 *
 * `isMobileDevice()` is `width < 700` and is true inside a desktop iframe
 * fixed at 600px. Classifying that window as a phone made
 * `shouldRestrictExpressToNativeWallet` drop Google Pay. `is-mobile` matches
 * phone UAs only (tablets excluded), so a desktop, an iPad, and a Chromebook
 * stay WEB at any width, while a phone in landscape stays IOS/ANDROID because
 * the UA does not change when the phone rotates.
 *
 * Layout still uses `useIsMobile()` / `isLandscape()` directly, so this does
 * not shift any breakpoints.
 */
export const getDeviceType = () => {
  if (!isMobile()) return DeviceType.WEB;
  if (isAndroid()) return DeviceType.ANDROID;
  return DeviceType.IOS;
};

/**
 * iOS phone (`getDeviceType() === IOS`): an iPhone/iPod UA. A narrow desktop
 * window and an iPad are WEB. Used to decide whether Apple Pay should be
 * loaded eagerly with a reserved placeholder slot (iOS) vs. lazily surfaced
 * only once the PSP confirms support (everything else, e.g. macOS Safari /
 * iPad, which still get Apple Pay once detected).
 */
export const isIOS = () => getDeviceType() === DeviceType.IOS;

/**
 * TRUE iOS/iPadOS hardware, by user agent — broader than `isIOS()`, which is
 * phones only. Use this when the decision depends on the actual browser
 * engine, not the layout: every browser on iOS runs WebKit, which is what
 * the OR_BIBED_15 Google Pay bug (`checkout_stripe_direct_google_pay`) is
 * tied to. iPadOS 13+ Safari reports a macOS UA; the multi-touch probe
 * catches it.
 */
export const isIOSUserAgent = () =>
  /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
  (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

export const getCustomerCountry = () => {
  return navigator.language.split('-')[1];
};

export const isLandscape = () => {
  return (
    window.innerWidth > window.innerHeight &&
    // `>= 700` (not `> 700`) so the band is contiguous with `isMobileDevice()`
    // (`< 700`): at exactly 700 the old strict bound left a dead-zone that was
    // neither mobile nor landscape, dropping the layout into the desktop branch
    // on a phone-landscape viewport. 700 also matches the Tailwind `md`/`sm`
    // min-width:700 breakpoints + the `landscape:` CSS variant, so JS and CSS
    // now agree on the same boundary.
    window.innerWidth >= 700 &&
    window.innerWidth <= 1023 &&
    window.innerHeight <= 500
  );
};
